// frontend/src/icons.js
//
// The one module the app imports icons from. eslint's no-restricted-imports
// bans @phosphor-icons/react everywhere else (eslint.config.js), so this file
// is the complete inventory of every glyph the UI can draw — which is the
// point: one idea gets one glyph, and icons.test.jsx fails if a collapsed
// duplicate is re-exported. It cannot police duplication in general — two
// glyphs meaning one thing are structurally just two glyphs — so reading this
// list before adding to it is the only real guard.
//
// Naming rule: ONE name per glyph, and that name is Phosphor's. A glyph does
// not get a second, role-flavoured alias — "SearchIcon" and "MagnifyingGlass"
// coexisting is the drift this module exists to prevent. Two deliberate
// exceptions:
//
// 1. The directional four below. Their whole purpose is to HIDE the arrow
//    direction behind the action, so the raw glyphs are not re-exported and
//    the lint rule keeps them unreachable. Data flows OUT of the app on
//    export (arrow up), INTO it on import (arrow down). Literal file
//    transfers keep the browser's perspective: download = down, upload = up.
//
// 2. MenuIcon, which is not a Phosphor glyph at all. Phosphor draws on a
//    256-unit grid, so at the sizes we render nothing lands on a whole pixel;
//    for curved glyphs that antialiasing reads as smoothness, but for a
//    hamburger's three horizontal bars it reads as blur. MenuIcon is drawn on
//    the pixel grid instead — see components/common/menuGlyphBars.js. It takes
//    the same props as everything here, so call sites cannot tell the
//    difference and should not have to.

export { default as MenuIcon } from './components/common/MenuGlyph';

export {
  UploadSimple as ExportIcon,
  DownloadSimple as ImportIcon,
  DownloadSimple as DownloadIcon,
  UploadSimple as UploadIcon,
} from '@phosphor-icons/react';

export {
  Archive, ArrowDown, ArrowElbowDownLeft, ArrowUp, ArrowsClockwise,
  Barcode, Bell, BookOpen, CalendarBlank, CalendarCheck,
  Camera, CaretDoubleDown, CaretDoubleUp, CaretDown, CaretLeft,
  CaretRight, ChartBar, ChartLine, ChartLineUp, Check,
  CheckCircle, ClipboardText, Clock, ClockCounterClockwise, Coins,
  Copy, Cube, CurrencyCircleDollar, CurrencyDollar, CursorClick,
  DotsThree,
  EnvelopeSimple, Eye,
  EyeSlash, FileXls, GearSix, GridFour, Handshake,
  HeartStraight, House, Image, Info, Keyboard,
  Lightning, ListBullets, ListChecks, Lock,
  MagnifyingGlass, MapPin, MinusCircle, Moon,
  Package, PaperPlaneTilt, PencilSimple, Play, Plus,
  Power, Prohibit, Question, Receipt,
  ShieldCheck, ShoppingCart, Sparkle, SpinnerGap,
  Storefront, Sun, Tag, Target, Trash,
  TrendDown, Truck, UserCircle, Warning, WarningCircle,
  Wrench, X, XCircle,
} from '@phosphor-icons/react';

/**
 * Every size an icon may be drawn at. No icon carries a literal number —
 * icons.test.jsx fails the build if one does.
 *
 * `display` deliberately breaks the t-shirt naming because it is not another
 * step up the same axis — it is the icon standing in for a picture (an empty
 * state, a dialog's header mark, usually weight="thin"), never a control.
 *
 * These are ICON sizes only. MUI's `size` prop on CircularProgress measures a
 * different thing and is out of scope.
 */
export const ICON = Object.freeze({
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  xxl: 24,
  display: 48,
});
